<template>
    <form ref="loginForm">
        <div class="form-control">
            <label for="email" class="label">
                <span class="label-text">
                    {{ $t('customer.loginForm.mailTitle') }}
                </span>
            </label>
            <input
                id="email"
                ref="emailInput"
                v-model="email"
                autocomplete="email"
                required
                name="username"
                type="email"
                inputmode="email"
                class="input input-bordered w-full"
            >
        </div>

        <div v-if="!isPasswordRecoveryMode" class="form-control mt-4">
            <label for="password" class="label">
                <span class="label-text">
                    {{ $t('customer.loginForm.passwordTitle') }}
                </span>
            </label>
            <div class="join">
                <input
                    id="password"
                    v-model="password"
                    required
                    name="password"
                    autocomplete="current-password"
                    :type="showPassword ? 'text' : 'password'"
                    class="join-item input input-bordered w-full border-r-0"
                >
                <div
                    role="button"
                    class="join-item btn px-3 btn-outline border-border border-l-0 shadow-none"
                    tabindex="0"
                    :aria-pressed="showPassword"
                    :aria-label="showPassword ? $t('customer.passwordResetForm.hideInput') : $t('customer.passwordResetForm.showInput')"
                    @click="showPassword = !showPassword"
                    @keydown.enter.prevent="showPassword = !showPassword"
                >
                    <MiscIcon v-if="!showPassword" name="eye" class="w-5 h-5" />
                    <MiscIcon v-else name="eye-off" class="w-5 h-5" />
                </div>
            </div>
        </div>

        <div
            role="button"
            class="link link-primary text-sm mt-2 font-semibold"
            tabindex="0"
            @click="isPasswordRecoveryMode = !isPasswordRecoveryMode"
            @keydown.enter="isPasswordRecoveryMode = !isPasswordRecoveryMode"
        >
            <span v-if="!isPasswordRecoveryMode">{{ $t('customer.loginForm.passwordReset') }}</span>
            <span v-if="isPasswordRecoveryMode">{{ $t('customer.loginForm.back') }}</span>
        </div>

        <template v-if="!isPasswordRecoveryMode">
            <button
                type="submit"
                class="btn btn-primary btn-block mt-6"
                :disabled="loading"
                data-cy="customer-login-button"
                @click.prevent="onLoginClick()"
            >
                <span v-if="loading" class="loading" />
                <span v-text="loading ? $t('customer.loginForm.loading') : $t('customer.state.logIn')" />
            </button>

            <!-- Magic link login button -->
            <CustomerLoginLink
                v-model:email="email"
                :validate="validateEmailField"
                class="mt-4"
            />
        </template>

        <button
            v-if="isPasswordRecoveryMode"
            type="submit"
            class="btn btn-secondary btn-block mt-6"
            :disabled="loading"
            @click.prevent="onResetPasswordClick()"
        >
            <span v-if="loading" class="loading" />
            <span v-text="loading ? $t('customer.loginForm.loading') : $t('customer.loginForm.reset')" />
        </button>
    </form>
</template>

<script setup lang="ts">
import { ApiClientError } from '@shopware/api-client'

const { t } = useI18n()

const email = defineModel<string>('email', { default: '' })

const { validateForm, validateField } = formValidation()
const { showNotification } = useNotification()
const password = ref()
const loginForm = ref()
const emailInput = ref<HTMLInputElement>()

const { login } = useUser()
const { resetPassword } = useCustomerPassword()
const isPasswordRecoveryMode = ref(false)
const loading = ref(false)
const showPassword = ref(false)
const { recaptcha } = useRecaptchaV3()
const { handleLoginSuccess } = useLoginSuccess()
const $config = useRuntimeConfig()
const basePath = ($config?.app?.baseURL || '/') === '/' ? '' : ($config?.app?.baseURL || '')

defineExpose({
    isPasswordRecoveryMode
})

// The magic link is sent to the e-mail address alone, so validating the whole form
// would wrongly demand the password field sitting above the button.
async function validateEmailField () {
    if (!emailInput.value) {
        return false
    }

    return await validateField(emailInput.value)
}

async function onLoginClick () {
    try {
        loading.value = true

        const isNotABot = await recaptcha()
        if (!isNotABot) {
            console.error('Recaptcha failed, please try again.')
            showNotification(t('misc.recaptchaFailed'), 'error', true)
            loading.value = false
            return
        }

        const isValid = await validateForm(loginForm.value)

        if (!isValid) {
            loading.value = false
            return
        }

        await login(
            {
                username: email.value,
                password: password.value
            }
        )

        // Handle post-login actions (analytics, wishlist merge, redirect)
        await handleLoginSuccess()

        // Clear form fields (redirect happens in handleLoginSuccess)
        email.value = ''
        password.value = ''
    } catch (e: unknown) {
        console.error(e)
        loading.value = false

        if (e instanceof ApiClientError) {
            const detail = e.details?.errors?.[0]?.detail
            showNotification(detail ?? e.message, 'error', true)
        } else {
            showNotification(e instanceof Error ? e.message : String(e), 'error', true)
        }
    }
}

async function onResetPasswordClick () {
    try {
        loading.value = true

        const isNotABot = await recaptcha()
        if (!isNotABot) {
            console.error('Recaptcha failed, please try again.')
            showNotification(t('misc.recaptchaFailed'), 'error', true)
            loading.value = false
            return
        }

        const isValid = await validateForm(loginForm.value)

        if (!isValid) {
            loading.value = false
            return
        }

        await resetPassword({
            email: email.value,
            storefrontUrl: `${window.location.origin}${basePath}`
        })

        showNotification(t('customer.loginForm.passwordResetNote'))

        email.value = ''
        password.value = ''
        loading.value = false
        isPasswordRecoveryMode.value = false
    } catch (e: unknown) {
        if (e instanceof Error && e.message === 'Not Found') {
            showNotification(t('customer.loginForm.passwordResetNote'))
        }

        loading.value = false
    }
}

// Navigating back restores this page from the
// bfcache with its JS state intact -- no lifecycle hook runs -- so the button would
// stay disabled. Release it here.
function onPageShow (event: PageTransitionEvent) {
    if (event.persisted) {
        loading.value = false
    }
}

onMounted(() => {
    window.addEventListener('pageshow', onPageShow)
})

onBeforeUnmount(() => {
    window.removeEventListener('pageshow', onPageShow)
})
</script>
